import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "../../../components/Button/Button";
import { SummaryCard } from "../../../components/SummaryCard/SummaryCard";
import { TextField } from "../../../components/FormField/TextField";
import { TimeField } from "../../../components/FormField/TimeField";
import { TextAreaField } from "../../../components/FormField/TextAreaField";
import { SelectField } from "../../../components/FormField/SelectField";
import { RadioCardGroup } from "../../../components/FormField/RadioCardGroup";
import { CheckboxField } from "../../../components/FormField/CheckboxField";
import { TurnstileWidget } from "../../../components/Turnstile/Turnstile";
import { bookingDetailsSchema } from "../../../validation/bookingRequestSchema";
import {
  CUSTOM_PRINT_DESIGN_LABELS,
  CUSTOM_PRINT_DESIGN_VALUES,
  EVENT_TYPE_LABELS,
  EVENT_TYPE_VALUES,
  GUEST_COUNT_LABELS,
  GUEST_COUNT_VALUES,
} from "../../../validation/eventTypes";
import { formatDateLong } from "../../../utils/time";
import { submitBookingRequest, ApiRequestError } from "../../../utils/apiClient";
import { publicEnv } from "../../../config/publicEnv";
import { TURNSTILE_EXPECTED_ACTION_PLACEHOLDER } from "../constants";
import type { BookingFormValues } from "../useBookingState";
import type { BookingSubmissionResult } from "../../../utils/apiClient";
import styles from "../BookingFlow.module.css";

interface StepFormProps {
  eventDate: string;
  formValues: BookingFormValues;
  onChange: (values: BookingFormValues) => void;
  idempotencyKey: string;
  onBack: () => void;
  onSubmitted: (result: BookingSubmissionResult) => void;
  onDateUnavailable: (message: string) => void;
}

const FIELD_ORDER = [
  "fullName",
  "email",
  "phone",
  "startTime",
  "endTime",
  "location",
  "eventType",
  "eventTypeOther",
  "guestCount",
  "customPrintDesign",
  "comments",
] as const;

const FIELD_LABELS: Record<(typeof FIELD_ORDER)[number], string> = {
  fullName: "Voor- en achternaam",
  email: "E-mailadres",
  phone: "Telefoonnummer",
  startTime: "Verwachte begintijd",
  endTime: "Verwachte eindtijd",
  location: "Locatie",
  eventType: "Type evenement",
  eventTypeOther: "Toelichting evenement",
  guestCount: "Aantal gasten",
  customPrintDesign: "Printontwerp",
  comments: "Opmerkingen",
};

export function StepForm({
  eventDate,
  formValues,
  onChange,
  idempotencyKey,
  onBack,
  onSubmitted,
  onDateUnavailable,
}: StepFormProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const errorSummaryRef = useRef<HTMLDivElement>(null);
  const formRenderedAtMsRef = useRef<number>(Date.now());

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileError, setTurnstileError] = useState(false);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  function updateField<K extends keyof BookingFormValues>(key: K, value: BookingFormValues[K]) {
    onChange({ ...formValues, [key]: value });
  }

  const guestCountOptions = useMemo(
    () => GUEST_COUNT_VALUES.map((value) => ({ value, label: GUEST_COUNT_LABELS[value] })),
    [],
  );
  const eventTypeOptions = useMemo(
    () => EVENT_TYPE_VALUES.map((value) => ({ value, label: EVENT_TYPE_LABELS[value] })),
    [],
  );
  const printDesignOptions = useMemo(
    () =>
      CUSTOM_PRINT_DESIGN_VALUES.map((value) => ({
        value,
        label: CUSTOM_PRINT_DESIGN_LABELS[value],
      })),
    [],
  );

  function focusFirstError(errors: Record<string, string>) {
    const firstField = FIELD_ORDER.find((field) => field in errors);
    const target = firstField ? document.getElementById(firstField) : null;
    if (target) {
      target.focus();
    } else {
      errorSummaryRef.current?.focus();
    }
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (submitting) return;

    setSubmitError(null);

    const candidate = { eventDate, ...formValues };
    const parseResult = bookingDetailsSchema.safeParse(candidate);

    if (!parseResult.success) {
      const errors: Record<string, string> = {};
      for (const issue of parseResult.error.issues) {
        const field = issue.path[0];
        if (typeof field === "string" && !(field in errors)) {
          errors[field] = issue.message;
        }
      }
      setFieldErrors(errors);
      focusFirstError(errors);
      return;
    }

    setFieldErrors({});

    if (!turnstileToken) {
      setSubmitError(
        "De beveiligingscontrole kon niet worden afgerond. Vernieuw de pagina en probeer het opnieuw.",
      );
      return;
    }

    setSubmitting(true);
    try {
      const result = await submitBookingRequest({
        ...parseResult.data,
        turnstileToken,
        honeypot,
        formRenderedAtMs: formRenderedAtMsRef.current,
        idempotencyKey,
      });
      onSubmitted(result);
    } catch (error) {
      if (error instanceof ApiRequestError) {
        if (error.status === 409) {
          onDateUnavailable(error.message);
          return;
        }
        if (error.fieldErrors) {
          setFieldErrors(error.fieldErrors);
          focusFirstError(error.fieldErrors);
        } else if (error.status === 0) {
          setSubmitError(error.message);
        } else if (error.code === "turnstile_failed") {
          setSubmitError(error.message);
        } else if (error.status >= 500) {
          setSubmitError(
            "Er ging iets mis bij het verwerken van je aanvraag. Probeer het opnieuw of neem contact met ons op via smile@demomentmaker.nl.",
          );
        } else {
          setSubmitError(error.message);
        }
      } else {
        setSubmitError("Er ging iets mis. Probeer het opnieuw.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  const errorEntries = Object.entries(fieldErrors);

  return (
    <section aria-labelledby="dmm-step-form-heading">
      <h2 id="dmm-step-form-heading" ref={headingRef} tabIndex={-1} className={styles.stepHeading}>
        Vertel ons over jouw evenement
      </h2>
      <span className={styles.dateChip}>Photobooth aanvragen voor {formatDateLong(eventDate)}</span>

      {errorEntries.length > 0 && (
        <div
          className={styles.errorSummary}
          ref={errorSummaryRef}
          tabIndex={-1}
          role="alert"
          aria-label="Controleer de gemarkeerde velden voordat je verdergaat"
        >
          <h3>Controleer de gemarkeerde velden voordat je verdergaat</h3>
          <ul>
            {errorEntries.map(([field, message]) => (
              <li key={field}>
                <a href={`#${field}`}>
                  {FIELD_LABELS[field as keyof typeof FIELD_LABELS] ?? field}: {message}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {submitError && (
        <div className={styles.errorSummary} role="alert">
          <p>{submitError}</p>
        </div>
      )}

      <form onSubmit={(event) => void handleSubmit(event)} noValidate>
        <div className={styles.formLayout}>
          <div className={styles.formColumn}>
            <h3 className={styles.fieldGroupTitle}>Contactgegevens</h3>
            <TextField
              id="fullName"
              label="Voor- en achternaam"
              value={formValues.fullName}
              onChange={(value) => updateField("fullName", value)}
              placeholder="Bijvoorbeeld Lisa de Vries"
              error={fieldErrors.fullName}
              required
              autoComplete="name"
            />
            <TextField
              id="email"
              label="E-mailadres"
              value={formValues.email}
              onChange={(value) => updateField("email", value)}
              placeholder="lisa@voorbeeld.nl"
              error={fieldErrors.email}
              required
              type="email"
              autoComplete="email"
            />
            <TextField
              id="phone"
              label="Telefoonnummer"
              value={formValues.phone}
              onChange={(value) => updateField("phone", value)}
              placeholder="06 12 34 56 78"
              error={fieldErrors.phone}
              type="tel"
              autoComplete="tel"
            />

            <h3 className={styles.fieldGroupTitle}>Evenementgegevens</h3>
            <TimeField
              id="startTime"
              label="Hoe laat begint het evenement ongeveer?"
              value={formValues.startTime}
              onChange={(value) => updateField("startTime", value)}
              helperText="Een schatting is voldoende."
              error={fieldErrors.startTime}
              required
            />
            <TimeField
              id="endTime"
              label="Hoe laat eindigt het evenement ongeveer?"
              value={formValues.endTime}
              onChange={(value) => updateField("endTime", value)}
              helperText="Een schatting is voldoende."
              error={fieldErrors.endTime}
              required
            />
            <CheckboxField
              id="endNextDay"
              label="De eindtijd is na middernacht"
              checked={formValues.endNextDay}
              onChange={(checked) => updateField("endNextDay", checked)}
            />

            <div style={{ marginTop: "var(--dmm-space-5)" }}>
              <TextField
                id="location"
                label="Waar vindt het evenement plaats?"
                value={formValues.location}
                onChange={(value) => updateField("location", value)}
                placeholder="Bijvoorbeeld De Schuur in Tilburg of Dorpsstraat 12, Breda"
                helperText="De naam van de locatie, plaats of het adres is voldoende."
                error={fieldErrors.location}
                required
              />
            </div>

            <RadioCardGroup
              name="eventType"
              legend="Wat voor evenement organiseer je?"
              value={formValues.eventType}
              onChange={(value) => updateField("eventType", value)}
              options={eventTypeOptions}
              error={fieldErrors.eventType}
              required
            />

            {formValues.eventType === "other" && (
              <TextField
                id="eventTypeOther"
                label="Wat voor evenement is het?"
                value={formValues.eventTypeOther}
                onChange={(value) => updateField("eventTypeOther", value)}
                error={fieldErrors.eventTypeOther}
                required
              />
            )}

            <SelectField
              id="guestCount"
              label="Hoeveel gasten verwacht je ongeveer?"
              value={formValues.guestCount}
              onChange={(value) => updateField("guestCount", value)}
              options={guestCountOptions}
              placeholder="Kies een optie"
              error={fieldErrors.guestCount}
            />

            <h3 className={styles.fieldGroupTitle}>Fotoprint</h3>
            <RadioCardGroup
              name="customPrintDesign"
              legend="Wil je een fotoprint met een persoonlijk ontwerp?"
              helperText="Denk bijvoorbeeld aan jullie namen, trouwdatum, bedrijfslogo, kleuren of thema."
              value={formValues.customPrintDesign}
              onChange={(value) => updateField("customPrintDesign", value)}
              options={printDesignOptions}
              error={fieldErrors.customPrintDesign}
              required
            />

            <h3 className={styles.fieldGroupTitle}>Aanvullende informatie</h3>
            <TextAreaField
              id="comments"
              label="Heb je aanvullende wensen of vragen?"
              value={formValues.comments}
              onChange={(value) => updateField("comments", value)}
              placeholder="Denk bijvoorbeeld aan het thema, bijzondere tijden, de gewenste uitstraling of de plek waar de photobooth komt te staan."
              maxLength={2000}
              error={fieldErrors.comments}
            />

            {/* Honeypot: onzichtbaar voor mensen, aantrekkelijk voor bots. */}
            <div className="dmm-visually-hidden" aria-hidden="true">
              <label htmlFor="website">Laat dit veld leeg</label>
              <input
                id="website"
                name="website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={honeypot}
                onChange={(event) => setHoneypot(event.target.value)}
              />
            </div>

            <p className={styles.privacyText}>
              Wij gebruiken je gegevens alleen om je aanvraag te beoordelen en hierover contact met
              je op te nemen. Lees meer in onze{" "}
              <a href={publicEnv.privacyPolicyUrl} target="_blank" rel="noreferrer">
                privacyverklaring
              </a>
              .
            </p>

            <div style={{ marginBottom: "var(--dmm-space-5)" }}>
              <TurnstileWidget
                siteKey={publicEnv.turnstileSiteKey}
                action={TURNSTILE_EXPECTED_ACTION_PLACEHOLDER}
                onVerify={setTurnstileToken}
                onExpire={() => setTurnstileToken(null)}
                onError={() => setTurnstileError(true)}
              />
              {/* Niet-visuele marker zodat geautomatiseerde tests kunnen wachten
                  tot de beveiligingscontrole daadwerkelijk is afgerond. */}
              <span
                className="dmm-visually-hidden"
                data-testid="turnstile-status"
                data-ready={turnstileToken ? "true" : "false"}
              />
              {turnstileError && (
                <p
                  role="alert"
                  style={{ color: "var(--dmm-color-danger)", marginTop: "var(--dmm-space-2)" }}
                >
                  De beveiligingscontrole kon niet worden geladen. Vernieuw de pagina en probeer het
                  opnieuw.
                </p>
              )}
            </div>

            <div className={styles.actionsRow}>
              <Button type="button" variant="secondary" onClick={onBack} disabled={submitting}>
                Terug naar de kalender
              </Button>
              <Button type="submit" isLoading={submitting}>
                Vraag beschikbaarheid en prijs aan
              </Button>
            </div>
          </div>

          <div className={styles.summaryColumn}>
            <SummaryCard
              eventDate={eventDate}
              startTime={formValues.startTime}
              endTime={formValues.endTime}
              endNextDay={formValues.endNextDay}
              location={formValues.location}
              eventTypeLabel={
                formValues.eventType === "other"
                  ? formValues.eventTypeOther
                  : formValues.eventType
                    ? EVENT_TYPE_LABELS[formValues.eventType as keyof typeof EVENT_TYPE_LABELS]
                    : ""
              }
              guestCountLabel={
                formValues.guestCount
                  ? GUEST_COUNT_LABELS[formValues.guestCount as keyof typeof GUEST_COUNT_LABELS]
                  : undefined
              }
              customPrintDesignLabel={
                formValues.customPrintDesign
                  ? CUSTOM_PRINT_DESIGN_LABELS[
                      formValues.customPrintDesign as keyof typeof CUSTOM_PRINT_DESIGN_LABELS
                    ]
                  : undefined
              }
            />
          </div>
        </div>
      </form>
    </section>
  );
}
